import { useCarSelectStore } from '../foundation/stores/carSelectStore';
import { useConfigStore } from '../foundation/stores/configStore';
import * as ApiConstant from '../foundation/apiConstant';
import { useCarComparisonStore } from '../foundation/stores/carComparisonStore';
import { UrlParameters } from '../foundation/enums/UrlParameters';
import { removeSpecialCharacters } from '../foundation/helpers';

interface ISearchResultsUrlOptions {
  includeDomain?: boolean;
  useCustomPath?: boolean;
  fromComparisonPage?: boolean;
}
/**
 * Provides functions to return the url structure based on the destination.
 * @returns
 */
export function useUrlBuilder() {
  const configStore = useConfigStore();
  const carSelectStore = useCarSelectStore();
  const comparisonStore = useCarComparisonStore();

  /**
   * Construct the url for navigating to the car comparison component with parameters
   *
   * @param useCustomPath Used to determine if the url generated will use the component's custom path within the sitecore content tree
   * @returns The destination url
   */
  function getCarComparisonUrl(vehicleIds: string[], useCustomPath = true): string {
    let url = '/compare-vehicles?vehicleIds=' + vehicleIds.join(',');

    if (ApiConstant.isProduction && useCustomPath) {
      url = `${location.origin}${configStore.CarComparisonUrl?.toLowerCase()}?vehicleIds=${vehicleIds.join(',')}`;
    }

    return url;
  }

  /**
   * Construct the url for navigating to the car details component with parameters
   *
   * @param carId The unique identifier of the car to be included in the url
   * @param includeDomain Include the domain in the url (this is ignored if using the production build)
   * @param useCustomPath Used to determine if the url generated will use the component's custom path within the sitecore content tree
   * @returns The destination url
   */
  function getCarDetailsUrl(carId: string, year: string, make: string, model: string, includeDomain = false, useCustomPath = true): string {
    let url = '/car-details';
    if (includeDomain) {
      url = `${location.origin}${url}`;
    }

    if (ApiConstant.isProduction && useCustomPath) {
      url = `${location.origin}${configStore.CarDetailsUrl?.toLowerCase()}`;
    }

    url += `/${year}/${removeSpecialCharacters(make?.toLowerCase())}/${removeSpecialCharacters(model?.toLowerCase())}/${carId}`; // grab the suffix/search criteria
    return url;
  }

  /**
   * Construct the url for navigating to the search results component with parameters constructed from the store
   *
   * @param includeDomain       Include the domain in the url (this is ignored if using the production build)
   * @param useCustomPath       Used to determine if the url generated will use the component's custom path within the sitecore content tree
   * @param fromComparisonPage  If coming from the comparison page, use the compared vehicle Makes to form search results URL.
   *
   * @returns The destination url
   */
  function getSearchResultsUrl(options: ISearchResultsUrlOptions = { includeDomain: false, useCustomPath: true, fromComparisonPage: false }): string {
    const { includeDomain, useCustomPath, fromComparisonPage } = options;
    let url = '/search-results';

    if (includeDomain) {
      url = `${location.origin}${url}`;
    }

    if (ApiConstant.isProduction && useCustomPath) {
      url = `${location.origin}${configStore.SearchResultsUrl?.toLowerCase()}`;
    }

    if (fromComparisonPage) {
      const vehicleMakes = comparisonStore.getComparedCarMakes;
      // We don't really care about anything except the Makes
      // So we hardcode the Year in the URL as that's how it's structure needs to be.
      url += `/${UrlParameters.Year}/_/${UrlParameters.Make}/${vehicleMakes.join('_')}`;
      return url;
    }

    url += carSelectStore.getUrlSearchCriteria; // grab the suffix/search criteria

    return url;
  }

  /**
   * Navigate to the home page
   *
   * @param searchModalOpen  Whether to open the search modal upon landing
   *
   * @returns The destination url
   */
  function getHomePageUrl(options: { searchModalOpen: boolean }) {
    const { searchModalOpen } = options;

    let url = '/';

    if (ApiConstant.isProduction) {
      url = `${location.origin}${configStore.HomePageUrl?.toLowerCase()}`;
    }

    if (searchModalOpen) {
      url += '?searchModalOpen=true';
    }

    return url;
  }

  function getExpertReviewsUrl(carId?: string, reviewId?: string, year?: string, make?: string, model?: string) {
    let url = '/car-reviews';

    if (ApiConstant.isProduction) {
      url = `${location.origin}${configStore.ExpertReviewsUrl?.toLowerCase()}`;
    }

    return (url += `/${year}/${removeSpecialCharacters(make?.toLowerCase())}/${removeSpecialCharacters(model?.toLowerCase())}/${carId}?reviewid=${reviewId}`);
  }

  return { getCarDetailsUrl, getSearchResultsUrl, getCarComparisonUrl, getHomePageUrl, getExpertReviewsUrl };
}
